import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { MenuGroup, SubmenuContainer } from '../frame/Submenu';
import { NotificationTasksContainer, NotificationTasksProps } from '../Notifications/NotificationTasksContainer';
import { QueryConsoleContainer } from '../QueryConsole';
import { ScoringSheetsManagementContainer, ScoringSheetsManagementProps } from './ScoringSheets';
import { EmailsPage } from '../emails/presentation/EmailsPage';
import { PageManagementContainer, PageManagementProps } from '../Pages';
import { FileListPage, FileListProps } from '../files';
import { AlternateEmailOutlined, AssignmentTurnedInOutlined, AttachFile, BookOutlined, BuildOutlined, Code, EmailOutlined, Group, PlaylistPlay, SubjectOutlined, Translate } from '@material-ui/icons';
import UserManagementForm, { IUserRole, UserManagementFormProps } from '../userManagement/UserManagementForm';
import { EventLogContainer } from '../EventLog/EventLogForm';
import { DictionariesList, DictionariesProps } from '../Dictionaries/DictionariesList';
import { SchemasPage } from '../schemed/SchemasPage';
import { DefaultUser, useUser } from '../../userContext/UserContext';
import { LocalizationMessagesContainer, LocalizationMessagesContainerProps } from '../localization';

interface Props {
    scoringSheets?: ScoringSheetsManagementProps;
    notificationTasks?: NotificationTasksProps;
    emails?: { apiPath: string };
    queryConsole?: { apiPath?: string };
    eventLog?: { apiPath: string };
    pages?: ({ key: string, label: ReactNode } & PageManagementProps)[];
    files?: ({ key: string, label: ReactNode } & FileListProps)[];
    users?: UserManagementFormProps<IUserRole>;
    dictionaries?: DictionariesProps;
    schemas?: {};
    messages?: LocalizationMessagesContainerProps;

    extraItems?: MenuGroup[];

    checkSectionAvailable?: Record<string, (user: DefaultUser) => boolean | undefined | null>;
    noCloseButton?: boolean;
}

export const SystemSubmenu = (props: Props) => {
    const { user } = useUser();

    const checkAccess = (key: string) => ((props.checkSectionAvailable || {})[key] || (() => true))(user);
    
    return (
        <SubmenuContainer
            noCloseButton={props.noCloseButton}
            groups={[
                ...(props.dictionaries && checkAccess("dictionaries") ? [{
                    key: "dictionaries",
                    icon: <BookOutlined />,
                    label: <FormattedMessage id="dictionaries.title" />,
                    items: [{ key: "dictionaries", label: "", component: () => <DictionariesList {...props.dictionaries as DictionariesProps} />}],
                },] : []),

                ...(props.pages && checkAccess("pages") ? [{
                    key: "pages",
                    icon: <SubjectOutlined />,
                    label: <FormattedMessage id="pages.pageListTitle" />,
                    items: props.pages.map(pp => ({ key: pp.key, label: pp.label, component: () => <PageManagementContainer {...pp} /> })),
                },] : []),

                ...(props.files && checkAccess("files") ? [{
                    key: "files",
                    icon: <AttachFile />,
                    label: <FormattedMessage id="files.pageTitle" />,
                    items: props.files.map(pp => ({ key: pp.key, label: pp.label, component: () => <FileListPage {...pp} /> })),
                },] : []),

                ...(props.scoringSheets && checkAccess("scoring-sheets") ? [{
                    key: "scoring-sheets",
                    icon: <AssignmentTurnedInOutlined />,
                    label: <FormattedMessage id="contests.scoring.sheets_list_title" />,
                    items: [{ key: "", label:  "xx", component: () => <ScoringSheetsManagementContainer {...props.scoringSheets as ScoringSheetsManagementProps} /> }]
                },] : []),

                ...(props.notificationTasks && checkAccess("notification-tasks") ? [{
                    key: "notification-tasks",
                    icon: <EmailOutlined />,
                    label: <FormattedMessage id="notifications.title" />,
                    items: [{ key: "", label:  "xx", component: () => <NotificationTasksContainer {...props.notificationTasks as NotificationTasksProps } /> }]
                },] : []),

                ...(props.emails && checkAccess("emails") ? [{
                    key: "emails",
                    icon: <AlternateEmailOutlined />,
                    label: <FormattedMessage id="emails.screen_title" />,
                    items: [{ key: "", label:  "xx", component: () => <EmailsPage apiPrefix={props.emails?.apiPath} /> }]
                },] : []),

                ...(props.users && checkAccess("users") ? [{
                    key: "users",
                    icon: <Group />,
                    label: <FormattedMessage id="userManagement.title" />,
                    items: [{ key: "", label:  "xx", component: () => <UserManagementForm {...props.users as UserManagementFormProps<IUserRole> } /> }]
                },] : []),

                ...(props.messages && checkAccess("messages") ? [{
                    key: "messages",
                    icon: <Translate />,
                    label: <FormattedMessage id="localization.messages.title" />,
                    items: [{ key: "", label:  "xx", component: () => <LocalizationMessagesContainer {...props.messages} /> }]
                },] : []),

                ...(props.eventLog && checkAccess("event-log") ? [{
                    key: "event-log",
                    icon: <PlaylistPlay />,
                    label: <FormattedMessage id="eventlog.screen_title" />,
                    items: [{ key: "", label:  "xx", component: () => <EventLogContainer {...props.eventLog as { apiPath: string} } /> }]
                },] : []),

                ...(props.queryConsole && checkAccess("query-console") ? [{
                    key: "query-console",
                    icon: <Code />,
                    label: <FormattedMessage id="queryconsole.screen_title" />,
                    items: [{ key: "", label:  "xx", component: () => <QueryConsoleContainer {...props.queryConsole } /> }]
                },] : []),

                ...(props.schemas && checkAccess("schemas") ? [{
                    key: "schemas",
                    icon: <BuildOutlined />,
                    label: <FormattedMessage id="schemas.title" />,
                    items: [{ key: "", label:  "xx", component: () => <SchemasPage /> }]
                },] : []),

                ...(props.extraItems || []).filter(x => checkAccess(x.key)),
            ]}
            />
    );
}
